import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  SilentRequest,
} from '@azure/msal-browser';
import {
  EMPTY,
  Observable,
  catchError,
  expand,
  forkJoin,
  map,
  of,
  reduce,
  shareReplay,
} from 'rxjs';
import { environment } from '../../environments/environment';

interface GraphDirectoryObjectsResponse {
  value?: Array<{ id?: string }>;
  '@odata.nextLink'?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private static readonly GRAPH_BASE = 'https://graph.microsoft.com/v1.0';

  // Admin access is granted to members and owners of the Entra group configured
  // via environment.adminGroupId. The resolved result is cached for the app's
  // lifetime to avoid repeated Microsoft Graph calls.
  private adminAccess$?: Observable<boolean>;

  constructor(
    private readonly msalService: MsalService,
    private readonly http: HttpClient,
  ) {}

  login(): void {
    this.msalService.loginRedirect({ scopes: [environment.apiScope] });
  }

  logout(): void {
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  }

  isLoggedIn(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  ssoSilent(): Observable<AuthenticationResult> {
    return this.msalService.ssoSilent({ scopes: [environment.apiScope] });
  }

  acquireTokenSilent(
    scopes: string[] = [environment.apiScope],
  ): Observable<AuthenticationResult> {
    const account = this.getActiveAccount();
    const request: SilentRequest = {
      scopes,
      account: account ?? undefined,
    };
    return this.msalService.acquireTokenSilent(request);
  }

    getUserName(): string {
    const account = this.msalService.instance.getActiveAccount();
    
    if (!account) {
      return 'User';
    }

    // Extract name from username (email) format: FirstName.LastName@domain.com
    if (account.username) {
      const emailPart = account.username.split('@')[0]; // Get "FirstName.LastName"
      const nameParts = emailPart.split('.'); // Split by dot
      
      if (nameParts.length >= 2) {
        // Capitalize first letter of each part
        const firstName = nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1).toLowerCase();
        const lastName = nameParts[1].charAt(0).toUpperCase() + nameParts[1].slice(1).toLowerCase();
        return `${firstName} ${lastName}`;
      }
    }

    // Fallback to account name or username
    return account.name || account.username || 'User';
  }


  getUserEmail(): string {
    const account = this.getActiveAccount();
    const emailClaim = account?.idTokenClaims?.['email'] as string | undefined;
    return account?.username ?? emailClaim ?? '';
  }

  getLoginHint(): string {
    const account = this.getActiveAccount();
    const loginHintClaim = account?.idTokenClaims?.['login_hint'] as
      | string
      | undefined;
    return loginHintClaim ?? account?.username ?? '';
  }

  /**
   * Emits whether the signed-in user is a member or owner of the admin Entra
   * group (environment.adminGroupId). The result is resolved once via Microsoft
   * Graph and cached. Emits false when signed out, unconfigured, or on error.
   */
  isAdmin(): Observable<boolean> {
    if (!this.adminAccess$) {
      this.adminAccess$ = this.resolveAdminAccess().pipe(shareReplay(1));
    }
    return this.adminAccess$;
  }

  private resolveAdminAccess(): Observable<boolean> {
    const groupId = environment.adminGroupId?.trim().toLowerCase();
    if (!this.isLoggedIn() || !groupId) {
      return of(false);
    }

    const memberGroups$ = this.fetchDirectoryObjectIds(
      `${AuthService.GRAPH_BASE}/me/memberOf/microsoft.graph.group?$select=id&$top=999`,
    );
    const ownedGroups$ = this.fetchDirectoryObjectIds(
      `${AuthService.GRAPH_BASE}/me/ownedObjects/microsoft.graph.group?$select=id&$top=999`,
    );

    return forkJoin([memberGroups$, ownedGroups$]).pipe(
      map(
        ([memberIds, ownedIds]) =>
          memberIds.has(groupId) || ownedIds.has(groupId),
      ),
      catchError(() => of(false)),
    );
  }

  /**
   * Fetches the set of directory object ids from a Microsoft Graph collection
   * endpoint, transparently following @odata.nextLink pagination.
   */
  private fetchDirectoryObjectIds(url: string): Observable<Set<string>> {
    return this.http.get<GraphDirectoryObjectsResponse>(url).pipe(
      expand((response) =>
        response['@odata.nextLink']
          ? this.http.get<GraphDirectoryObjectsResponse>(
              response['@odata.nextLink'],
            )
          : EMPTY,
      ),
      reduce((ids, response) => {
        for (const item of response.value ?? []) {
          if (item.id) {
            ids.add(item.id.toLowerCase());
          }
        }
        return ids;
      }, new Set<string>()),
    );
  }

  private getActiveAccount(): AccountInfo | null {
    return (
      this.msalService.instance.getActiveAccount() ??
      this.msalService.instance.getAllAccounts()[0] ??
      null
    );
  }
}
